import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  Grid,
  Chip,
  Fade,
  Skeleton,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Add as AddIcon,
  FilterList,
  LocationOn,
  PowerSettingsNew,
  Edit,
  Delete,
  EvStation,
  DeviceHub,
  Power,
  PowerOff,
  Refresh,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChargingStation,
  faMapMarkerAlt,
  faBolt,
  faPlug,
  faCog
} from "@fortawesome/free-solid-svg-icons";
import { turnOnCharger, turnOffCharger, getChargerSummary, getChargerStatus, setChargerVisibility } from "../../services/chargerService";
import { useMovilidadElectrica } from "../../context/MovilidadElectricaContext";
import CreateChargerModal from "./CreateChargerModal";
import EditChargerModal from "./EditChargerModal";
import DeleteChargerModal from "./DeleteChargerModal";
import { TUYA_ENERGY_CORRECTION_FACTOR, maybeCorrectTuyaKwh } from "@utils/energyCorrections";

const ControlCargadores = () => {
  const {
    chargers,
    refreshChargers,
    parkers,
    refreshParkers,
    loading,
    error,
  } = useMovilidadElectrica();
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [isCreateModalOpen, setICreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [chargerSummaries, setChargerSummaries] = useState({});
  const [chargerStatuses, setChargerStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  const formatChargerState = (charger, statusPayload) => {
    if (!statusPayload || typeof statusPayload !== 'object') return null;

    if (statusPayload.state === 'communication_error') {
      console.warn(`Communication error for charger ${charger?.charger_id}:`, statusPayload.error);
      return null; // fall back to summary status
    }

    if (charger?.controller_type === 'wallbox') {
      if (statusPayload.locked === true) return 'Bloqueado';
      if (statusPayload.locked === false) return 'Desbloqueado';
      if (statusPayload.state === 'on') return 'Desbloqueado';
      if (statusPayload.state === 'off') return 'Bloqueado';
      return statusPayload.state || 'Desconocido';
    }

    if (statusPayload.state === 'on') return 'Encendido';
    if (statusPayload.state === 'off') return 'Apagado';
    return statusPayload.state || 'Desconocido';
  };

  const reloadChargerStatus = async (chargerId) => {
    setStatusLoading(prev => ({ ...prev, [chargerId]: true }));
    try {
      const status = await getChargerStatus(chargerId);
      setChargerStatuses(prev => ({ ...prev, [chargerId]: status }));
    } catch (e) {
      console.error(`Error fetching status for charger ${chargerId}:`, e);
    } finally {
      setStatusLoading(prev => ({ ...prev, [chargerId]: false }));
    }
  };

  useEffect(() => {
    refreshParkers();
    const params = { page: currentPage, per_page: 10 };
    refreshChargers(params);
  }, [currentPage, refreshChargers, refreshParkers]);

  useEffect(() => {
    if (chargers.data) {
      chargers.data.forEach(charger => {
        getChargerSummary(charger.charger_id)
          .then(summary => {
            setChargerSummaries(prev => ({ ...prev, [charger.charger_id]: summary }));
          })
          .catch(error => {
            console.error(`Error fetching summary for charger ${charger.charger_id}:`, error);
          });
      });
    }
  }, [chargers.data]);

  const handleAction = async (action, chargerId) => {
    setFeedback({ type: "", message: "" });
    try {
      const userInfo = JSON.parse(sessionStorage.getItem('user'));
      
      const isAdmin = userInfo ? userInfo.is_admin : false;

      const chargerType = (chargers?.data || []).find(c => c.charger_id === chargerId)?.controller_type;
      const isWallbox = chargerType === 'wallbox';

      if (action === "on") {
        await turnOnCharger(chargerId, isAdmin);
      } else {
        await turnOffCharger(chargerId);
      }

      if (isWallbox) {
        setFeedback({ type: "success", message: `Cargador ${action === "on" ? "desbloqueado" : "bloqueado"} correctamente.` });
      } else {
        setFeedback({ type: "success", message: `Cargador ${action === "on" ? "encendido" : "apagado"} correctamente.` });
      }
      
      // Actualiza el estado inmediatamente
      getChargerSummary(chargerId)
        .then(summary => {
          setChargerSummaries(prev => ({ ...prev, [chargerId]: summary }));
        })
        .catch(error => {
          console.error(`Error fetching summary for charger ${chargerId}:`, error);
        });

      reloadChargerStatus(chargerId);

    } catch (error) {
      const chargerType = (chargers?.data || []).find(c => c.charger_id === chargerId)?.controller_type;
      const isWallbox = chargerType === 'wallbox';
      if (isWallbox) {
        setFeedback({ type: "error", message: `Error al ${action === "on" ? "desbloquear" : "bloquear"} el cargador.` });
      } else {
        setFeedback({ type: "error", message: `Error al ${action === "on" ? "encender" : "apagar"} el cargador.` });
      }
    }
  };

  const openEditModal = (charger) => {
    setSelectedCharger(charger);
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (charger) => {
    setSelectedCharger(charger);
    setIsDeleteModalOpen(true);
  };

  const [visibilityLoading, setVisibilityLoading] = useState({});

  // Ocultar un cargador lo retira del flujo de reserva de los usuarios normales:
  // su parqueadero deja de ofrecerse y no admite reservas nuevas. Las reservas
  // ya existentes se respetan.
  const handleToggleVisibility = async (charger) => {
    const next = !charger.is_visible;
    setVisibilityLoading((prev) => ({ ...prev, [charger.charger_id]: true }));
    try {
      await setChargerVisibility(charger.charger_id, next);
      setFeedback({
        type: "success",
        message: next
          ? `Cargador #${charger.charger_id} visible: ya se puede reservar.`
          : `Cargador #${charger.charger_id} oculto: no se ofrecerá a los usuarios.`,
      });
      await refreshChargers();
      await refreshParkers();
    } catch (err) {
      setFeedback({
        type: "error",
        message: err?.response?.data?.detail || "No se pudo cambiar la visibilidad del cargador.",
      });
    } finally {
      setVisibilityLoading((prev) => ({ ...prev, [charger.charger_id]: false }));
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <Fade in timeout={600}>
      <Box>
        {/* Modales */}
        <CreateChargerModal
          isOpen={isCreateModalOpen}
          onClose={() => setICreateModalOpen(false)}
          onChargerCreated={() => refreshChargers({ page: currentPage, per_page: 10 })}
        />
        <EditChargerModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          charger={selectedCharger}
          onChargerUpdated={() => refreshChargers({ page: currentPage, per_page: 10 })}
        />
        <DeleteChargerModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          charger={selectedCharger}
          onChargerDeleted={() => refreshChargers({ page: currentPage, per_page: 10 })}
        />


        {/* Lista de cargadores */}
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
          border: '1px solid rgba(0, 38, 66, 0.06)',
          mb: 3
        }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 3
            }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <EvStation sx={{ color: '#FFFFFF', fontSize: '1.2rem' }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#002642',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.125rem' }
                }}
              >
                Cargadores Disponibles
                {(chargers.data || []).length > 0 && (
                  <Chip
                    label={`${(chargers.data || []).length} cargador${(chargers.data || []).length !== 1 ? 'es' : ''}`}
                    size="small"
                    sx={{
                      ml: 2,
                      backgroundColor: 'rgba(219, 150, 0, 0.1)',
                      color: '#DB9600',
                      fontWeight: 600
                    }}
                  />
                )}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setICreateModalOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #DB9600 0%, #B8860B 100%)',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  px: { xs: 2.5, sm: 3 },
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  boxShadow: '0 4px 12px rgba(219, 150, 0, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B8860B 0%, #996F0A 100%)',
                    boxShadow: '0 6px 16px rgba(219, 150, 0, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {isMobile ? 'Crear' : 'Crear Cargador'}
              </Button>
            </Box>

            {/* Estados de carga/error/vacío */}
            {loading ? (
              <Box sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  {[...Array(3)].map((_, index) => (
                    <Grid item xs={12} md={6} lg={4} key={index}>
                      <Card sx={{ 
                        borderRadius: 3,
                        border: '1px solid rgba(0, 38, 66, 0.06)'
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="60%" height={24} />
                              <Skeleton variant="text" width="40%" height={20} />
                            </Box>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Stack direction="row" spacing={1}>
                            <Skeleton variant="rounded" width={80} height={32} />
                            <Skeleton variant="rounded" width={80} height={32} />
                            <Skeleton variant="rounded" width={60} height={32} />
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : error ? (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'rgba(219, 150, 0, 0.05)',
                  border: '1px solid rgba(219, 150, 0, 0.2)',
                  '& .MuiAlert-icon': { color: '#DB9600' }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  No hay cargadores disponibles
                </Typography>
              </Alert>
            ) : (chargers.data || []).length === 0 ? (
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 87, 122, 0.05)',
                  border: '1px solid rgba(0, 87, 122, 0.1)',
                  '& .MuiAlert-icon': { color: '#00577A' }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  No hay cargadores asignados al parqueadero seleccionado
                </Typography>
              </Alert>
            ) : (
              /* Grid de cargadores */
              <Grid container spacing={3}>
                {(chargers.data || []).map((charger) => (
                  <Grid item xs={12} md={6} lg={4} key={charger.charger_id}>
                    <Card sx={{ 
                      borderRadius: 3,
                      border: '1px solid rgba(0, 38, 66, 0.06)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0, 38, 66, 0.12)',
                        transform: 'translateY(-2px)'
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        {/* Header del cargador */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2, 
                          mb: 2 
                        }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #DB9600 0%, #B8860B 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <FontAwesomeIcon 
                              icon={faChargingStation} 
                              style={{ color: '#FFFFFF', fontSize: '1.2rem' }} 
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                color: '#002642',
                                fontWeight: 600,
                                fontSize: '1rem',
                                mb: 0.5
                              }}
                            >
                              Cargador #{charger.charger_id}
                            </Typography>
                            {charger.is_visible === false && (
                              <Chip
                                icon={<VisibilityOff sx={{ fontSize: '0.9rem' }} />}
                                label="Oculto para usuarios"
                                size="small"
                                sx={{
                                  mb: 0.5,
                                  backgroundColor: 'rgba(158, 158, 158, 0.18)',
                                  color: '#5F6368',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: '22px',
                                  '& .MuiChip-icon': { color: '#5F6368' }
                                }}
                              />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <DeviceHub sx={{ color: '#75787B', fontSize: '0.9rem' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#75787B',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Device: {charger.device_id}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOn sx={{ color: '#75787B', fontSize: '0.9rem' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#75787B',
                                  fontSize: '0.85rem'
                                }}
                              >
                                {(parkers.data || []).find(p => p.charger_id === charger.charger_id)?.location || 'Sin ubicación'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Charger Status and Energy */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#002642' }}>
                              Estado:{' '}
                              {(() => {
                                const statusText = formatChargerState(charger, chargerStatuses[charger.charger_id]);
                                if (statusText) return statusText;
                                return chargerSummaries[charger.charger_id]?.status || 'Cargando...';
                              })()}
                            </Typography>

                            <Tooltip title="Recargar estado" placement="top">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => reloadChargerStatus(charger.charger_id)}
                                  disabled={Boolean(statusLoading[charger.charger_id])}
                                  sx={{ color: '#002642' }}
                                >
                                  {statusLoading[charger.charger_id] ? (
                                    <CircularProgress size={16} sx={{ color: '#002642' }} />
                                  ) : (
                                    <Refresh fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#002642' }}>
                            Energía Total: {(() => {
                              const summary = chargerSummaries[charger.charger_id];
                              const total = summary?.total_energy_kwh;
                              if (typeof total !== 'number') return 'Cargando...';

                              const factor = (typeof summary?.energy_correction_factor === 'number')
                                ? summary.energy_correction_factor
                                : TUYA_ENERGY_CORRECTION_FACTOR;

                              const corrected = maybeCorrectTuyaKwh(total, charger.controller_type, {
                                factor,
                                alreadyCorrected: Boolean(summary?.energy_is_corrected),
                              });

                              return `${Number(corrected).toFixed(2)} kWh`;
                            })()}
                          </Typography>
                        </Box>

                        {/* Botones de acción */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Power />}
                            onClick={() => handleAction('on', charger.charger_id)}
                            sx={{
                              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              px: 2,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
                                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)'
                              }
                            }}
                          >
                            {charger.controller_type === 'wallbox' ? 'Desbloquear' : 'Encender'}
                          </Button>
                          
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PowerOff />}
                            onClick={() => handleAction('off', charger.charger_id)}
                            sx={{
                              background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              px: 2,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #D32F2F 0%, #C62828 100%)',
                                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)'
                              }
                            }}
                          >
                            {charger.controller_type === 'wallbox' ? 'Bloquear' : 'Apagar'}
                          </Button>
                          
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={!!visibilityLoading[charger.charger_id]}
                            startIcon={charger.is_visible === false ? <VisibilityOff /> : <Visibility />}
                            onClick={() => handleToggleVisibility(charger)}
                            title={
                              charger.is_visible === false
                                ? "Actualmente oculto: no se ofrece al reservar. Pulsa para mostrarlo."
                                : "Actualmente visible. Pulsa para ocultarlo a los usuarios normales."
                            }
                            sx={{
                              borderColor: charger.is_visible === false ? '#9E9E9E' : '#2E7D32',
                              color: charger.is_visible === false ? '#9E9E9E' : '#2E7D32',
                              fontWeight: 600,
                              px: 2,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              '&:hover': {
                                borderColor: charger.is_visible === false ? '#9E9E9E' : '#2E7D32',
                                backgroundColor: charger.is_visible === false
                                  ? 'rgba(158, 158, 158, 0.04)'
                                  : 'rgba(46, 125, 50, 0.04)'
                              }
                            }}
                          >
                            {charger.is_visible === false ? 'Mostrar' : 'Ocultar'}
                          </Button>

                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => openEditModal(charger)}
                            sx={{
                              borderColor: '#002642',
                              color: '#002642',
                              fontWeight: 600,
                              px: 2,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              '&:hover': {
                                borderColor: '#002642',
                                backgroundColor: 'rgba(0, 38, 66, 0.04)'
                              }
                            }}
                          >
                            Editar
                          </Button>
                          
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Delete />}
                            onClick={() => openDeleteModal(charger)}
                            sx={{
                              borderColor: '#F44336',
                              color: '#F44336',
                              fontWeight: 600,
                              px: 2,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              '&:hover': {
                                borderColor: '#F44336',
                                backgroundColor: 'rgba(244, 67, 54, 0.04)'
                              }
                            }}
                          >
                            Eliminar
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Paginación */}
        {(chargers.data || []).length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            mb: 2 
          }}>
            <Pagination
              count={chargers.pages || 1}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#002642',
                  fontWeight: 500,
                  '&.Mui-selected': {
                    backgroundColor: '#002642',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#00577A'
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 38, 66, 0.04)'
                  }
                }
              }}
            />
          </Box>
        )}

        {/* Feedback */}
        {feedback.message && (
          <Alert 
            severity={feedback.type}
            sx={{ 
              mt: 3,
              borderRadius: 2,
              fontWeight: 500
            }}
            onClose={() => setFeedback({ type: "", message: "" })}
          >
            {feedback.message}
          </Alert>
        )}
      </Box>
    </Fade>
  );
};

export default ControlCargadores;
